import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios, { AxiosError } from 'axios'

/**
 * Heartbeat account snapshot for an org's profile tab.
 *
 * Mirrors the shape returned by Heartbeat's
 * `GET /api/v1/grobotic/accounts/{id}/snapshot` endpoint, proxied through
 * Grobotic's backend at `/aws/heartbeat/organization/{PK}/snapshot`.
 *
 * Error responses from the backend use stable `code` values so the UI can
 * render a specific message per failure mode (account unlinked, network
 * unreachable, Heartbeat down) rather than a generic "something broke".
 */
export interface HeartbeatSnapshot {
  account: {
    id: number
    name: string | null
    type: string | null
    active: boolean
    archived: boolean
    website_url: string | null
    phone: string | null
    address: {
      street: string | null
      city: string | null
      state: string | null
      zipcode: string | null
      country: string | null
    }
    external_ids: {
      netsuite_id: string | null
      click_up_id: string | null
      freshsale_id: string | null
      mxb_id: string | null
      loop_id: string | null
    }
    parent_account_id: number | null
    created_at: string | null
    updated_at: string | null
  }
  subscriptions: Array<{
    id: number
    name: string | null
    type: string | null
    status: string | null
    frequency: string | null
    price: string | number | null
    quantity: string | number | null
    recurring_amount: string | number | null
    total: string | number | null
    is_mrr: boolean
    is_service_mrr: boolean
    start_date: string | null
    end_date: string | null
    termination_date: string | null
    external_id: string | null
    service_line: {
      id: number
      code: string | null
      name: string | null
      abbreviation: string | null
    } | null
    subscription_item: {
      id: number
      display_name: string | null
      service_line: string | null
    } | null
  }>
  contracts: Array<{
    id: number
    start_date: string | null
    note: string | null
    service_lines: string[] | string | null
    external_id: string | null
    external_source: string | null
    created_at: string | null
    updated_at: string | null
  }>
  recent_submissions: Array<{
    id: number
    type: string | null
    status: string | null
    altered_status: string | null
    churn_scope: string | null
    requested_date: string | null
    effective_date: string | null
    notes: string | null
    created_at: string | null
  }>
  counts: {
    subscriptions_total: number
    subscriptions_active: number
    contracts_total: number
    submissions_recent: number
  }
  /** Where the snapshot came from. 'sandbox' only appears in dev when the
   *  resolver fell back to the local Sail database. */
  source?: 'prod' | 'sandbox'
  /** Only set when source='sandbox' — why we fell back. */
  fallback_reason?: 'prod_not_found' | 'prod_name_mismatch' | 'prod_unavailable'
}

export interface HeartbeatSnapshotResponse {
  success: true
  org_pk: string
  heartbeat_account_id: string | number
  snapshot: HeartbeatSnapshot
}

export type HeartbeatSnapshotErrorCode =
  | 'org_not_found'
  | 'heartbeat_account_unlinked'
  | 'heartbeat_account_not_found'
  | 'heartbeat_unreachable'
  | 'heartbeat_error'
  | 'heartbeat_query_failed'
  | 'heartbeat_api_key_missing'
  | 'org_lookup_failed'
  | 'invalid_request'

export interface HeartbeatSnapshotError {
  success: false
  code: HeartbeatSnapshotErrorCode
  error: string
  org_pk?: string
  org_name?: string | null
  status?: number
  body?: unknown
}

export const fetchHeartbeatSnapshot = async (PK: string): Promise<HeartbeatSnapshotResponse> => {
  const response = await axios.get<HeartbeatSnapshotResponse>(
    `${baseUrl}/aws/heartbeat/organization/${encodeURIComponent(PK)}/snapshot`,
  )
  return response.data
}

export const useOrgHeartbeatSnapshot = (PK: string | undefined) => {
  return useQuery<HeartbeatSnapshotResponse, AxiosError<HeartbeatSnapshotError>>({
    queryKey: ['heartbeat-snapshot', PK],
    queryFn: (): Promise<HeartbeatSnapshotResponse> => fetchHeartbeatSnapshot(PK as string),
    enabled: Boolean(PK),
    // The snapshot can shift if Heartbeat data changes; 30s is short enough
    // to feel fresh and long enough that tab-switching back and forth
    // doesn't hammer the proxy.
    staleTime: 30_000,
    retry: false,
  })
}
